import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Smartphone,
  PlusCircle,
  QrCode,
  KeyRound,
  Monitor,
  MousePointer2,
  Copy,
  Download,
  ChevronDown,
  Check
} from "lucide-react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import AuthLayout from "./AuthPageLayout";
import PageMeta from "../../../../shared/ui/common/PageMeta";

export default function GoogleAuthenticatorGuidePage() {
  const { t } = useTranslation();
  const { breakpoint } = useBreakpoint();
  const isMobileView = breakpoint === 'mobile' || breakpoint === 'sm' || breakpoint === 'md' || breakpoint === 'lg';
  const [activeTab, setActiveTab] = useState<'mobile' | 'extension'>('mobile');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const mobileSteps = [
    {
      icon: <Download className="w-6 h-6" />,
      title: t("profile.googleAuthenticatorGuide.step1.title"),
      description: t("profile.googleAuthenticatorGuide.step1.description"),
    },
    {
      icon: <PlusCircle className="w-6 h-6" />,
      title: t("profile.googleAuthenticatorGuide.step2.title"),
      description: t("profile.googleAuthenticatorGuide.step2.description"),
    },
    {
      icon: <QrCode className="w-6 h-6" />,
      title: t("profile.googleAuthenticatorGuide.step3.title"),
      description: t("profile.googleAuthenticatorGuide.step3.description"),
    },
    {
      icon: <KeyRound className="w-6 h-6" />,
      title: t("profile.googleAuthenticatorGuide.step4.title"),
      description: t("profile.googleAuthenticatorGuide.step4.description"),
    },
  ];

  const extensionSteps = [
    {
      icon: <Download className="w-6 h-6" />,
      title: t("profile.googleAuthenticatorGuide.chromeExtension.step1.title"),
      description: (
        <span>
          {t("profile.googleAuthenticatorGuide.chromeExtension.step1.description")}{" "}
          <a 
            href="https://chromewebstore.google.com/detail/google-authenticator/ebhcbenbgjmaebpgbldimndmfomjmphd" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-brand-600 hover:text-brand-700 underline font-bold"
          >
            Chrome Web Store
          </a>
        </span>
      ),
    },
    {
      icon: <QrCode className="w-6 h-6" />,
      title: t("profile.googleAuthenticatorGuide.chromeExtension.step2.title"),
      description: t("profile.googleAuthenticatorGuide.chromeExtension.step2.description"),
    },
    {
      icon: <MousePointer2 className="w-6 h-6" />,
      title: t("profile.googleAuthenticatorGuide.chromeExtension.step3.title"),
      description: t("profile.googleAuthenticatorGuide.chromeExtension.step3.description"),
    },
    {
      icon: <Copy className="w-6 h-6" />,
      title: t("profile.googleAuthenticatorGuide.chromeExtension.step4.title"),
      description: t("profile.googleAuthenticatorGuide.chromeExtension.step4.description"),
    },
  ];

  const steps = activeTab === 'mobile' ? mobileSteps : extensionSteps;

  return (
    <>
      <PageMeta title={t("profile.googleAuthenticatorGuide.title")} />
      <AuthLayout>
        <div className="w-full max-w-2xl mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-content-primary mb-4">
              {t("profile.googleAuthenticatorGuide.title")}
            </h1>
            <p className="text-lg text-content-tertiary">
              {t("profile.googleAuthenticatorGuide.subtitle")}
            </p>
          </div>

          <div className="bg-brand-500/5 rounded-3xl p-6 mb-10 border border-brand-500/10 backdrop-blur-sm">
            <p className="text-content-secondary leading-relaxed font-medium">
              {t("profile.googleAuthenticatorGuide.intro")}
            </p>
          </div>

          {/* TABS SYSTEM */}
          <div className="w-full mb-10 relative z-30">
            {isMobileView ? (
              /* 📱 Zen UX Mobile Tab Selector (Custom Dropdown) */
              <div className="max-w-sm mx-auto sm:mx-0">
                <div className="relative">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className={`
                      w-full h-12 flex items-center justify-between px-5 rounded-2xl border transition-all duration-300 shadow-theme-sm
                      ${isMobileMenuOpen 
                        ? "bg-white dark:bg-brand-600 border-brand-500 ring-4 ring-brand-500/10" 
                        : "bg-white/50 dark:bg-white/[0.03] border-gray-200 dark:border-white/5 backdrop-blur-md"
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-4 h-4 flex items-center justify-center ${isMobileMenuOpen ? "text-brand-600 dark:text-white" : "text-brand-500"}`}>
                        {activeTab === 'mobile' ? <Smartphone className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
                      </span>
                      <span className={`text-sm font-bold ${isMobileMenuOpen ? "text-brand-600 dark:text-white" : "text-content-primary"}`}>
                        {t(`profile.googleAuthenticatorGuide.tabs.${activeTab}`)}
                      </span>
                    </div>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isMobileMenuOpen ? "rotate-180 text-brand-600 dark:text-white" : "text-content-tertiary"}`} />
                  </button>

                  <AnimatePresence>
                    {isMobileMenuOpen && (
                      <>
                        <div 
                          className="fixed inset-0 z-40 bg-transparent" 
                          onClick={() => setIsMobileMenuOpen(false)} 
                        />
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -10 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="absolute left-0 right-0 mt-2 z-50 bg-surface-raised border border-gray-100 dark:border-white/5 rounded-2xl shadow-theme-xl overflow-hidden backdrop-blur-sm"
                        >
                          <div className="p-1.5 space-y-1">
                            {[
                              { id: 'mobile', label: t("profile.googleAuthenticatorGuide.tabs.mobile"), icon: <Smartphone className="w-4 h-4" /> },
                              { id: 'extension', label: t("profile.googleAuthenticatorGuide.tabs.extension"), icon: <Monitor className="w-4 h-4" /> }
                            ].map((tab) => (
                              <button
                                key={tab.id}
                                onClick={() => {
                                  setActiveTab(tab.id as 'mobile' | 'extension');
                                  setIsMobileMenuOpen(false);
                                }}
                                className={`
                                  w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200
                                  ${activeTab === tab.id 
                                    ? "bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 font-bold" 
                                    : "text-content-secondary hover:bg-gray-50 dark:hover:bg-white/5 font-medium"
                                  }
                                `}
                              >
                                <div className="flex items-center gap-3">
                                  <span className={`w-4 h-4 flex items-center justify-center ${activeTab === tab.id ? "text-brand-600 dark:text-brand-400" : "text-content-tertiary"}`}>
                                    {tab.icon}
                                  </span>
                                  <span className="text-sm">{tab.label}</span>
                                </div>
                                {activeTab === tab.id && <Check className="w-4 h-4" />}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            ) : (
              /* 💻 Desktop Pill Navigation */
              <div className="flex p-1.5 bg-gray-100 dark:bg-white/5 rounded-2xl relative z-10 w-max overflow-x-auto no-scrollbar shadow-inner border border-gray-200 dark:border-white/5">
                <button
                  onClick={() => setActiveTab('mobile')}
                  className={`relative flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-300 flex-none whitespace-nowrap font-bold ${
                    activeTab === 'mobile' 
                      ? "text-brand-600 dark:text-white" 
                      : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"
                  }`}
                >
                  {activeTab === 'mobile' && (
                    <motion.div
                      layoutId="active-tab-bg"
                      className="absolute inset-0 bg-white dark:bg-brand-600 rounded-xl shadow-raised border border-gray-200 dark:border-brand-500/20"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <Smartphone className="w-4 h-4 relative z-10" />
                  <span className="relative z-10">{t("profile.googleAuthenticatorGuide.tabs.mobile")}</span>
                </button>
                <button
                  onClick={() => setActiveTab('extension')}
                  className={`relative flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-300 flex-none whitespace-nowrap font-bold ${
                    activeTab === 'extension' 
                      ? "text-brand-600 dark:text-white" 
                      : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"
                  }`}
                >
                  {activeTab === 'extension' && (
                    <motion.div
                      layoutId="active-tab-bg"
                      className="absolute inset-0 bg-white dark:bg-brand-600 rounded-xl shadow-raised border border-gray-200 dark:border-brand-500/20"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <Monitor className="w-4 h-4 relative z-10" />
                  <span className="relative z-10">{t("profile.googleAuthenticatorGuide.tabs.extension")}</span>
                </button>
              </div>
            )}
          </div>

          <AnimatePresence mode="wait">
            <motion.div 
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="grid gap-6"
            >
              {steps.map((step, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white dark:bg-white/5 rounded-3xl p-6 border border-gray-100 dark:border-white/10 shadow-raised hover:shadow-raised hover:border-brand-500/30 transition-all duration-500"
                >
                  <div className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                      {step.icon}
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-content-primary group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                        {step.title}
                      </h3>
                      <p className="text-content-tertiary leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

        </div>
      </AuthLayout>
    </>
  );
}
